import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import { Box, Divider, Fade, IconButton, Menu, MenuItem, type SxProps, type Theme, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type KeyboardEvent, type MouseEvent, type ReactNode, type SyntheticEvent, useEffect, useId, useRef, useState } from "react";
import { ACTION_MENU, ALPHA, CONTROL_HEIGHT, FONT_SIZE, ICON_SIZE, INK, LETTER_SPACING, MOTION, NEUTRAL, SHADOW, SURFACE } from "../../../theme/tokens";
import { MenuItemWithReason } from "./MenuItemWithReason";
import { portalOverlayProps } from "./portalOverlay";

/**
 * One pick in a submenu: a value to set (a step to move to — `selected` marks the current one) or, in a submenu
 * that only groups related actions (a CV's Show / Download / Replace / Remove), an action of its own.
 */
export type ActionMenuChoice = {
	id: string;
	label: string;
	/** A quieter second line under the label — what the choice is about at a glance (a workplace and its years). */
	secondaryLabel?: string;
	/** A small leading mark — the choice's colour dot, an avatar, or an action's icon. */
	leading?: ReactNode;
	/** Where the record is now: drawn with a check, announced as checked, and picking it does nothing. */
	selected?: boolean;
	/** `danger` paints the choice in the error palette, as on a top-level action (Remove). */
	tone?: "danger";
	/** Run inside the click itself, as `ActionMenuAction.immediate` — for a choice that opens a file picker. */
	immediate?: boolean;
	onSelect: () => void;
	dataTestId: string;
};

type ActionMenuItemBase = {
	id: string;
	/** Already translated. */
	label: string;
	icon?: ReactNode;
	/** `danger` paints the row in the error palette (Delete, Remove). */
	tone?: "danger";
	/** Starts a new group: a hairline above it. */
	groupStart?: boolean;
	/** Trailing slot: a spinner while the row is still being worked out, a shortcut, a count. */
	endAdornment?: ReactNode;
	dataTestId: string;
};

export type ActionMenuAction = ActionMenuItemBase & {
	onSelect: () => void;
	/** Present ⇒ shown but unavailable, and this is the tooltip saying why (`MenuItemWithReason`). */
	disabledReason?: ReactNode;
	/**
	 * Run inside the click itself instead of once the menu has closed — for an action that needs the user's
	 * gesture (opening a file picker). Off: the menu closes and hands focus back to the "⋯" first, so a dialog
	 * the action opens returns focus there too.
	 */
	immediate?: boolean;
};

/** A row that opens a list of choices beside the menu: on hover, on a click or tap, or with → / Enter. */
export type ActionMenuSubmenu = ActionMenuItemBase & {
	submenu: {
		title: string;
		choices: ActionMenuChoice[];
		/**
		 * `pick` (DEFAULT — Move to step): one value of several, so choices are `menuitemradio`s and one is checked.
		 * `actions`: related actions tucked under one row (CV ▸ Show, Download, Replace, Remove), so plain `menuitem`s.
		 */
		kind?: "pick" | "actions";
	};
};

export type ActionMenuItem = ActionMenuAction | ActionMenuSubmenu;

/** What a caller's own trigger (`renderTrigger`) is handed: the open state and the props that wire it to the menu. */
export type ActionMenuTriggerProps = {
	open: boolean;
	onClick: () => void;
	"aria-haspopup": "menu";
	"aria-expanded": boolean;
	"aria-controls": string | undefined;
	"data-testid": string;
};

type Props = {
	items: ActionMenuItem[];
	/** Accessible name of the icon-only trigger, already translated ("Actions for Maria Alvarez"). */
	triggerLabel: string;
	/** The trigger's glyph. DEFAULT: a horizontal ellipsis. */
	triggerIcon?: ReactNode;
	/**
	 * How the trigger looks. `quiet` (DEFAULT — every card and row menu) is the small muted icon button that sits
	 * inside content. `segment` is the trailing half of a split button: a full `CONTROL_HEIGHT.STANDARD` square in the
	 * secondary button's outline, whose start edge fuses with the button right before it — options for THAT
	 * action ("Show CV" · ⋯), in a row of page-header buttons. Give that button `SEGMENT_LEAD_SX` and render both
	 * inside one inline-flex box, so they never wrap apart.
	 */
	triggerVariant?: "quiet" | "segment";
	/**
	 * Opt-in: the caller's own trigger in place of the "⋯" icon button — a labelled button ("Set holiday
	 * group ▾"). The menu then opens under the trigger's start edge instead of its end. `triggerLabel` still
	 * names the menu; `triggerIcon`, `triggerVariant` and `sx` (all for the icon button) are not used.
	 */
	renderTrigger?: (trigger: ActionMenuTriggerProps) => ReactNode;
	/** The menu opened or closed — for items whose content loads only once someone looks (`open`). */
	onOpenChange?: (open: boolean) => void;
	/** The pointer reached, or focus landed on, the trigger: warm whatever the menu is about to read. */
	onIntent?: () => void;
	/** Trigger test id; the menu panel is `{dataTestId}-menu`, an open submenu's panel `{dataTestId}-submenu`. */
	dataTestId: string;
	sx?: SxProps<Theme>;
};

const isSubmenu = (item: ActionMenuItem): item is ActionMenuSubmenu => "submenu" in item;

/** Every press and key inside the menu stays inside it: an ancestor card or row must not open, select or drag. */
const stop = (event: SyntheticEvent) => event.stopPropagation();

/** The arrow that leads INTO a submenu in the element's reading direction (→ in LTR, ← in RTL); the other goes back. */
const intoKey = (element: Element): string => (getComputedStyle(element).direction === "rtl" ? "ArrowLeft" : "ArrowRight");
const backKey = (element: Element): string => (getComputedStyle(element).direction === "rtl" ? "ArrowRight" : "ArrowLeft");

type SubmenuSide = "start" | "end";

/** The physical side a box starts on in its reading direction: left, or right on a right-to-left page. */
const startSideOf = (element: Element): "left" | "right" => (getComputedStyle(element).direction === "rtl" ? "right" : "left");

/**
 * The side of its row a submenu opens on: the right, unless the panel wouldn't fit there (a card at the
 * board's right edge), then the left — Popover would otherwise shift it back over the menu it came from.
 * Physical sides, since Popover's anchor origins are physical; a right-to-left page simply has more room left.
 */
const submenuSide = (row: HTMLElement): SubmenuSide => {
	const { left, right } = row.getBoundingClientRect();
	const roomRight = window.innerWidth - right;
	return roomRight >= ACTION_MENU.MIN_WIDTH_PX || roomRight >= left ? "end" : "start";
};

/** A directional glyph (→, ←) points the other way when the app reads right to left (`body.dir`). */
const FLIP_IN_RTL_SX = { "[dir=rtl] &": { transform: "scaleX(-1)" } } as const;

/** How a row reads: an ordinary action, a destructive one, or the choice the record is on now. */
type RowTone = "default" | "danger" | "selected" | "disabled";

/**
 * The floating surface both panels share: the dropdown panel's border, radius and shadow. No height cap here: the
 * main list is a fixed set of actions and shows them all (Popover still keeps it inside the window); only the
 * submenu, whose length is the data's (a job's steps), is capped.
 */
const PANEL_SX = {
	minWidth: ACTION_MENU.MIN_WIDTH_PX,
	borderRadius: 1.5,
	border: `1px solid ${NEUTRAL[200]}`,
	boxShadow: SHADOW.POPOVER_FLOAT,
} as const;

const rowSx =
	(tone: RowTone): SxProps<Theme> =>
	(theme) => {
		const danger = tone === "danger";
		const ink = { default: INK.PRIMARY, danger: theme.palette.error.main, selected: theme.palette.primary.dark, disabled: INK.DISABLED }[tone];
		const glyph = { default: INK.MUTED, danger: theme.palette.error.main, selected: INK.MUTED, disabled: INK.DISABLED }[tone];
		return {
			gap: 1.25,
			mx: 0.5,
			px: 1.25,
			minHeight: CONTROL_HEIGHT.COMPACT,
			borderRadius: 1,
			fontSize: FONT_SIZE.EMPHASIS,
			fontWeight: tone === "selected" ? 600 : 400,
			color: ink,
			"& .MuiSvgIcon-root": { fontSize: FONT_SIZE.TITLE_SM, color: glyph, flexShrink: 0 },
			"&:hover, &.Mui-focusVisible": {
				backgroundColor: alpha(danger ? theme.palette.error.main : theme.palette.primary.main, ALPHA.SELECTED_OVERLAY),
			},
		};
	};

/**
 * For the button a `segment` trigger attaches to: squares its end corners, so the two read as one split control.
 * Logical corners, so the seam stays between them in a right-to-left page too.
 */
export const SEGMENT_LEAD_SX = { borderStartEndRadius: 0, borderEndEndRadius: 0 } as const;

/** The quiet "⋯" a card or row carries: a `MICRO` icon button, brand-tinted on hover and while its menu is open. */
const quietTriggerSx =
	(open: boolean): SxProps<Theme> =>
	(theme) => ({
		width: CONTROL_HEIGHT.MICRO,
		height: CONTROL_HEIGHT.MICRO,
		borderRadius: 1,
		color: INK.MUTED,
		backgroundColor: open ? alpha(theme.palette.primary.main, ALPHA.CONTROL_TINT_HOVER) : SURFACE,
		"& .MuiSvgIcon-root": { fontSize: FONT_SIZE.TITLE_SM },
		"@media (hover: hover) and (pointer: fine)": {
			"&:hover": { backgroundColor: alpha(theme.palette.primary.main, ALPHA.CONTROL_TINT_HOVER), color: theme.palette.primary.dark },
		},
		"&:active": { transform: "scale(0.94)" },
		"@media (pointer: coarse)": { minWidth: CONTROL_HEIGHT.COMPACT, minHeight: CONTROL_HEIGHT.COMPACT },
	});

/**
 * The split button's trailing half: the secondary `SystemButton`'s outline, ink and hover, at its height. Its start
 * border is dropped — the lead button's end border is the one seam between them — and its start corners squared.
 */
const segmentTriggerSx =
	(open: boolean): SxProps<Theme> =>
	(theme) => {
		const openTint = alpha(theme.palette.primary.main, ALPHA.CONTROL_TINT_HOVER);
		return {
			width: CONTROL_HEIGHT.STANDARD,
			height: CONTROL_HEIGHT.STANDARD,
			flexShrink: 0,
			borderRadius: 1,
			borderStartStartRadius: 0,
			borderEndStartRadius: 0,
			border: `1px solid ${INK.SECONDARY}`,
			borderInlineStart: "none",
			color: open ? theme.palette.primary.dark : INK.SECONDARY,
			backgroundColor: open ? openTint : SURFACE,
			// A glyph that carries the button on its own, at the weight of the lead button's icon.
			"& .MuiSvgIcon-root": { fontSize: ICON_SIZE.CONTROL },
			// The lead button's hover; while open it keeps the open tint rather than flicker to grey under the pointer.
			"&:hover": { backgroundColor: open ? openTint : NEUTRAL[100] },
			"&:active": { transform: "scale(0.97)" },
		};
	};

const RowContent = ({ icon, label, secondaryLabel, end }: { icon?: ReactNode; label: string; secondaryLabel?: string; end?: ReactNode }) => (
	<>
		{icon}
		<Box component="span" sx={{ flex: 1, minWidth: 0 }}>
			{secondaryLabel === undefined ? (
				label
			) : (
				// Two lines: the label at full weight, the detail under it in muted caption ink.
				<Box component="span" sx={{ display: "flex", flexDirection: "column", py: 0.25 }}>
					<Box component="span" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
						{label}
					</Box>
					<Box component="span" sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED, lineHeight: 1.35 }}>
						{secondaryLabel}
					</Box>
				</Box>
			)}
		</Box>
		{end}
	</>
);

/**
 * The "⋯" overflow menu for a card or a row: a quiet icon button that opens a short list of everything that
 * can be done to the record, grouped by hairlines. Built for a trigger that sits INSIDE something clickable
 * or draggable (a board card, a table row): every press and key inside it stops here, so opening it, or
 * picking from it, never opens, selects or drags what it sits on.
 *
 * A row is an action (optionally unavailable with a stated reason, via `MenuItemWithReason`) or a submenu: its
 * choices open in a second panel beside the row as soon as the pointer rests on it (a click or tap opens it
 * too, for touch), and stay open while the pointer crosses into them; resting on another row closes it.
 * → / Enter opens it with focus on the first choice, ← or Escape closes it back to its row, following the
 * reading direction. A submenu either picks one value (`kind: "pick"`, radios, the current one checked) or groups
 * related actions under one row (`kind: "actions"` — each choice with its own icon, `tone` and `immediate`).
 * Actions and choices run after the menu has closed and given focus back to the trigger (so a dialog they open
 * returns focus there too), unless `immediate`.
 *
 * Same floating surface as the dropdown panel, a 180ms rise-and-fade (reduced-motion safe), brand-tinted hover.
 * Geometry from `ACTION_MENU`; every string comes from the caller.
 */
export const ActionMenu = ({ items, triggerLabel, triggerIcon, triggerVariant, renderTrigger, onOpenChange, onIntent, dataTestId, sx }: Props) => {
	const menuId = useId();
	const triggerRef = useRef<HTMLButtonElement>(null);
	// A caller's own trigger may not take a ref, so the menu hangs from the wrapper it sits in (exactly its size).
	const wrapperRef = useRef<HTMLSpanElement>(null);
	const [open, setOpen] = useState(false);
	// A caller's trigger leads its menu from its start edge, which is the right edge in RTL. Read on each open.
	// DEFAULT: left until the first open; it is only read while the menu is open.
	const [triggerStart, setTriggerStart] = useState<"left" | "right">("left");
	// The open submenu, the row it hangs from, which side of it it opens on, and whether the keyboard opened it
	// (so focus moves into it).
	const [submenu, setSubmenu] = useState<{ id: string; row: HTMLElement; side: SubmenuSide; byKeyboard: boolean } | null>(null);
	const pending = useRef<(() => void) | null>(null);

	// Runs after the close has committed — the menu's focus trap has handed focus back to the trigger by then.
	useEffect(() => {
		if (open || pending.current === null) return;
		const action = pending.current;
		pending.current = null;
		action();
	}, [open]);

	const setMenuOpen = (next: boolean) => {
		setOpen(next);
		setSubmenu(null);
		onOpenChange?.(next);
	};

	/** A top-level action or a submenu choice: inside the click when `immediate`, else once the menu has closed. */
	const run = ({ onSelect, immediate }: { onSelect: () => void; immediate?: boolean }) => {
		if (immediate) onSelect();
		else pending.current = onSelect;
		setMenuOpen(false);
	};

	const openSubmenu = (item: ActionMenuSubmenu, row: HTMLElement, byKeyboard: boolean) => {
		const side = submenuSide(row);
		// Against the latest state, not this render's: Enter's keydown opens it by keyboard and MUI then fires a
		// synthetic click in the same tick, which must not demote it to a hover-open (focus would stay behind).
		setSubmenu((current) => (current?.id === item.id && !byKeyboard ? current : { id: item.id, row, side, byKeyboard }));
	};
	/** Back to the row it hangs from — focus too, when the keyboard is driving. */
	const closeSubmenu = (refocusRow: boolean) => {
		if (refocusRow) submenu?.row.focus();
		setSubmenu(null);
	};
	// Resting on any other row of the menu (a disabled one included) closes an open submenu, as in any desktop menu.
	const closeSubmenuOnOtherRow = (event: MouseEvent<HTMLUListElement>) => {
		const row = event.target instanceof Element ? event.target.closest("li") : null;
		if (submenu !== null && row !== null && row !== submenu.row) setSubmenu(null);
	};

	const activeSubmenu = items.find((item): item is ActionMenuSubmenu => isSubmenu(item) && item.id === submenu?.id);

	// A flat array, not Fragments: MUI's MenuList walks its direct children to move focus between rows.
	const mainRows = items.flatMap((item, index) => {
		const divider = item.groupStart && index > 0 ? [<Divider key={`${item.id}-divider`} sx={{ my: 0.5, borderColor: NEUTRAL[200] }} />] : [];
		const danger = item.tone === "danger";
		if (isSubmenu(item)) {
			return [
				...divider,
				<MenuItem
					key={item.id}
					aria-haspopup="menu"
					aria-expanded={submenu?.id === item.id}
					selected={submenu?.id === item.id}
					onMouseEnter={(event) => openSubmenu(item, event.currentTarget, false)}
					onClick={(event) => openSubmenu(item, event.currentTarget, false)}
					onKeyDown={(event: KeyboardEvent<HTMLLIElement>) => {
						if (event.key !== intoKey(event.currentTarget) && event.key !== "Enter") return;
						event.preventDefault();
						openSubmenu(item, event.currentTarget, true);
					}}
					sx={rowSx(danger ? "danger" : "default")}
					data-testid={item.dataTestId}
				>
					<RowContent icon={item.icon} label={item.label} end={item.endAdornment ?? <ChevronRightRoundedIcon sx={{ ml: 1, ...FLIP_IN_RTL_SX }} />} />
				</MenuItem>,
			];
		}
		if (item.disabledReason) {
			return [
				...divider,
				<MenuItemWithReason key={item.id} disabledReason={item.disabledReason} onClick={item.onSelect} sx={rowSx("disabled")} dataTestId={item.dataTestId}>
					<RowContent icon={item.icon} label={item.label} end={item.endAdornment} />
				</MenuItemWithReason>,
			];
		}
		return [
			...divider,
			<MenuItem key={item.id} onClick={() => run(item)} sx={rowSx(danger ? "danger" : "default")} data-testid={item.dataTestId}>
				<RowContent icon={item.icon} label={item.label} end={item.endAdornment} />
			</MenuItem>,
		];
	});

	const submenuRows = activeSubmenu && [
		<Typography
			key="title"
			component="li"
			role="presentation"
			sx={{
				px: 2,
				pt: 0.75,
				pb: 0.5,
				fontSize: FONT_SIZE.LABEL_XS,
				fontWeight: 700,
				letterSpacing: LETTER_SPACING.LABEL,
				textTransform: "uppercase",
				color: INK.MUTED,
			}}
		>
			{activeSubmenu.submenu.title}
		</Typography>,
		...activeSubmenu.submenu.choices.map((choice) => (
			<MenuItem
				key={choice.id}
				// DEFAULT: `pick` — the one value-picking submenu (Move to step) this was built for.
				{...(activeSubmenu.submenu.kind === "actions" ? { role: "menuitem" } : { role: "menuitemradio", "aria-checked": choice.selected === true })}
				onClick={() => {
					if (choice.selected) {
						setMenuOpen(false);
						return;
					}
					run(choice);
				}}
				onKeyDown={(event: KeyboardEvent<HTMLLIElement>) => {
					if (event.key !== backKey(event.currentTarget)) return;
					event.preventDefault();
					closeSubmenu(true);
				}}
				sx={rowSx(choice.selected === true ? "selected" : choice.tone === "danger" ? "danger" : "default")}
				data-testid={choice.dataTestId}
			>
				<RowContent
					icon={choice.leading}
					label={choice.label}
					secondaryLabel={choice.secondaryLabel}
					end={choice.selected ? <CheckRoundedIcon sx={{ ml: 1, "&&": { color: "primary.main" } }} aria-hidden /> : undefined}
				/>
			</MenuItem>
		)),
	];

	return (
		// Not a control: it only keeps the menu's events from reaching the card or row it sits on.
		<Box ref={wrapperRef} component="span" onPointerDown={stop} onMouseDown={stop} onClick={stop} onKeyDown={stop} sx={{ display: "inline-flex" }}>
			{renderTrigger ? (
				renderTrigger({
					open,
					onClick: () => {
						if (!open && wrapperRef.current !== null) setTriggerStart(startSideOf(wrapperRef.current));
						setMenuOpen(!open);
					},
					"aria-haspopup": "menu",
					"aria-expanded": open,
					"aria-controls": open ? menuId : undefined,
					"data-testid": dataTestId,
				})
			) : (
				<IconButton
					ref={triggerRef}
					size="small"
					aria-label={triggerLabel}
					aria-haspopup="menu"
					aria-expanded={open}
					aria-controls={open ? menuId : undefined}
					onClick={() => setMenuOpen(!open)}
					onPointerEnter={onIntent}
					onFocus={onIntent}
					data-testid={dataTestId}
					sx={[
						// DEFAULT: `quiet` — what every card and row menu has always been.
						triggerVariant === "segment" ? segmentTriggerSx(open) : quietTriggerSx(open),
						(theme) => ({
							transition: `background-color ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}, transform ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}`,
							"&.Mui-focusVisible": { boxShadow: `0 0 0 2px ${theme.palette.primary.main}` },
							"@media (prefers-reduced-motion: reduce)": { transition: "none", "&:active": { transform: "none" } },
						}),
						...(sx === undefined ? [] : Array.isArray(sx) ? sx : [sx]),
					]}
				>
					{/* DEFAULT: the ellipsis is what "more actions" looks like everywhere; a caller may pass another glyph. */}
					{triggerIcon ?? <MoreHorizRoundedIcon />}
				</IconButton>
			)}
			<Menu
				id={menuId}
				anchorEl={renderTrigger ? wrapperRef.current : triggerRef.current}
				open={open}
				onClose={() => setMenuOpen(false)}
				// The "⋯" sits at the end of its row, so its menu opens toward the start; a labelled trigger leads its menu.
				anchorOrigin={{ vertical: "bottom", horizontal: renderTrigger ? triggerStart : "right" }}
				transformOrigin={{ vertical: "top", horizontal: renderTrigger ? triggerStart : "right" }}
				slots={{ transition: Fade }}
				transitionDuration={{ enter: MOTION.DURATION.ENTRANCE_SM, exit: MOTION.DURATION.INLINE_EXIT }}
				slotProps={{
					root: { ...portalOverlayProps("action-menu") },
					paper: {
						// A data attribute rides the slot props through MUI's spread; the slot type has no index signature for it.
						...({ "data-testid": `${dataTestId}-menu` } as Record<string, string>),
						sx: {
							mt: 0.5,
							...PANEL_SX,
							"@keyframes actionMenuRise": { from: { transform: "translateY(-4px) scale(0.985)" }, to: { transform: "none" } },
							animation: `actionMenuRise ${MOTION.DURATION.ENTRANCE_SM}ms ${MOTION.EASE_OUT}`,
							"@media (prefers-reduced-motion: reduce)": { animation: "none" },
						},
					},
					list: { dense: true, sx: { py: 0.5 }, "aria-label": triggerLabel, onMouseOver: closeSubmenuOnOtherRow },
				}}
			>
				{mainRows}
			</Menu>
			<Menu
				open={open && activeSubmenu !== undefined && submenu !== null}
				anchorEl={submenu?.row ?? null}
				onClose={() => closeSubmenu(true)}
				// Beside its row, top edges aligned, on whichever side has room (`submenuSide`).
				anchorOrigin={{ vertical: "top", horizontal: submenu?.side === "start" ? "left" : "right" }}
				transformOrigin={{ vertical: "top", horizontal: submenu?.side === "start" ? "right" : "left" }}
				// Opened by hover, focus stays on the main list (so ↑ / ↓ keep working there); by keyboard, it moves in.
				autoFocus={submenu?.byKeyboard === true}
				disableAutoFocus={submenu?.byKeyboard !== true}
				disableEnforceFocus
				disableRestoreFocus
				// No backdrop of its own and no pointer capture outside its panel: the main list underneath stays
				// hoverable, and a click elsewhere reaches the main menu's backdrop, which closes both.
				hideBackdrop
				slots={{ transition: Fade }}
				transitionDuration={{ enter: MOTION.DURATION.HOVER, exit: MOTION.DURATION.INLINE_EXIT }}
				slotProps={{
					root: { ...portalOverlayProps("action-menu-submenu"), sx: { pointerEvents: "none" } },
					paper: {
						...({ "data-testid": `${dataTestId}-submenu` } as Record<string, string>),
						sx: { mx: 0.5, mt: -0.5, pointerEvents: "auto", ...PANEL_SX, maxHeight: ACTION_MENU.MAX_HEIGHT_PX },
					},
					list: { dense: true, sx: { py: 0.5 }, "aria-label": activeSubmenu?.submenu.title },
				}}
			>
				{submenuRows}
			</Menu>
		</Box>
	);
};

export default ActionMenu;
